import Select from 'react-select';
import { InternalExternalLink, FileLink } from './read-more-link-type';
import { getPosts, decodeUnicode } from '@helpers/post-block-utils';
import { READ_MORE_TYPE_LINK } from '@constants';
import { __experimentalNumberControl as NumberControl } from '@wordpress/components';

function registerNotificationOptionsPlugin({
  i18n: { __ },
  element: { useState, useEffect },
  data: { dispatch, withSelect },
  components: {
    SelectControl,
    TextControl,
    ToggleControl,
    TextareaControl,
    Button,
  },
  editor: { PluginDocumentSettingPanel },
  blockEditor: { MediaUpload, MediaUploadCheck },
}) {
  const { B3RG_IN_EX_LINK, B3RG_FI_LINK, B3RG_NONE } = READ_MORE_TYPE_LINK;

  const NotificationOptionsSettings = ({
    postMeta,
    currentPostId,
    featuredImageUrl,
  }) => {
    const {
      notification_enabled: featuredMetaValue,
      notification_enabled_post_list: notificationPageListMetaValue,
      notification_text: notificationTextMetaValue,
      notification_expiration_time: notificationExpirationMetaValue,
      learn_more_label: learnMoreLabelMetaValue,
      learn_more_type: learnMoreTypeMetaValue,
      learn_more_link: learnMoreLinkMetaValue,
      learn_more_link_file: learnMoreLinkFileMetaValue,
      notification_logo: notificationLogoUrlMetaValue,
    } = postMeta;

    const showPopupMetaValue = false;

    const [notificationText, setNotificationText] = useState(
      notificationTextMetaValue,
    );

    const [learnMoreLabel, setLearnMoreLabel] = useState(
      learnMoreLabelMetaValue || 'Read More',
    );
    const [learnMoreType, setLearnMoreType] = useState(
      learnMoreTypeMetaValue || B3RG_IN_EX_LINK,
    );

    const [notificationExpirationTime, setNotificationExpirationTime] =
      useState(notificationExpirationMetaValue || 24);
    const [notificationLogoUrl, setNotificationLogoUrl] = useState(
      notificationLogoUrlMetaValue,
    );

    const [notificationEnabled, setNotificationEnabled] =
      useState(featuredMetaValue);
    const pageIds = notificationPageListMetaValue;
    const [notificationEnabledPagedList, setNotificationEnabledPagedList] =
      useState([]);
    const [pagesListOptions, setPagesListOptions] = useState([]);

    const [isPageListLoading, setPageListLoading] = useState(true);

    const setPagesData = (postList) => {
      const notificationOptions = [];
      const pagesArray = [];
      postList.forEach((post) => {
        const {
          id,
          title: { rendered },
        } = post;

        if (currentPostId !== id && rendered) {
          const postData = { value: id, label: decodeUnicode(rendered) };
          notificationOptions.push(postData);
          if (pageIds.includes(parseInt(id))) {
            pagesArray.push(postData);
          }
        }
      });
      setNotificationEnabledPagedList(pagesArray);
      setPagesListOptions(notificationOptions);
      setPageListLoading(false);
    };

    useEffect(() => {
      setPageListLoading(true);
      const query = {
        per_page: -1,
        order: 'asc',
        status: 'publish',
      };
      getPosts('page', setPagesData, query);
    }, []); // eslint-disable-line

    const onMetaUpdate = (field, value) => {
      // update metadata.
      dispatch('core/editor').editPost({
        meta: {
          [field]: value,
        },
      });
    };

    useEffect(() => {
      // Updating the learn more type meta value during the build time
      onMetaUpdate('learn_more_type', learnMoreType);
    }, [learnMoreType]);

    return (
      <PluginDocumentSettingPanel
        className="common-post-options"
        name="common-post-options"
        title={__('Notification Options', 'b3rg')}
      >
        <div className="components-base-control">
          <label
            htmlFor="featured-post"
            className={'components-base-control__label'}
          >
            {__('Enable Notification in Pages', 'b3rg')}
          </label>
          <ToggleControl
            id="featured-post"
            label={__('Disable | Enable')}
            checked={notificationEnabled}
            onChange={(value) => {
              setNotificationEnabled(value);
              onMetaUpdate('notification_enabled', value);
            }}
          />
        </div>

        {notificationEnabled && (
          <>
            <div className="components-base-control featured-in-page">
              <label
                htmlFor="featured-in-page"
                className={'components-base-control__label'}
              >
                {__('Enable In', 'b3rg')}
              </label>
              <Select
                id="featured-in-page"
                isMulti
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
                placeholder={__('Enabled In (Pages)', '')}
                options={pagesListOptions}
                value={notificationEnabledPagedList}
                isLoading={isPageListLoading}
                onChange={(selectedPages) => {
                  let selectedPageList = [];
                  if (selectedPages) {
                    selectedPageList = selectedPages.map((selectedPage) => {
                      return parseInt(selectedPage.value);
                    });
                  }
                  setNotificationEnabledPagedList(selectedPages);
                  onMetaUpdate(
                    'notification_enabled_post_list',
                    selectedPageList,
                  );
                }}
              />
            </div>
            <div className="components-base-control">
              <label
                htmlFor="placeholder-image"
                className="notification-image-label"
              >
                {__('Notification Logo', 'b3rg')}
              </label>
              <MediaUploadCheck>
                <MediaUpload
                  id="placeholder-image"
                  value={notificationLogoUrl}
                  allowedTypes={['image']}
                  onSelect={(value) => {
                    setNotificationLogoUrl(value.url);
                    onMetaUpdate('notification_logo', value.url);
                  }}
                  render={({ open }) => (
                    <>
                      {notificationLogoUrl && (
                        <div className="media-preview__notification-img">
                          <img src={notificationLogoUrl} alt="" />
                        </div>
                      )}

                      {notificationLogoUrl === '' && (
                        <Button
                          className={'editor-post-featured-image__toggle'}
                          onClick={open}
                        >
                          Choose an image
                        </Button>
                      )}

                      {notificationLogoUrl && (
                        <>
                          <Button onClick={open} isPrimary>
                            {__('Replace image', '')}
                          </Button>
                          <Button
                            onClick={() => {
                              setNotificationLogoUrl('');
                              onMetaUpdate('notification_logo', '');
                            }}
                            isSecondary
                            isDestructive
                          >
                            {__('Remove image', '')}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                />
              </MediaUploadCheck>
            </div>
          </>
        )}
        <TextareaControl
          label={__('Notification Text', 'b3rg')}
          help={__('Type the notification content here.', 'b3rg')}
          value={notificationText}
          onChange={(value) => {
            setNotificationText(value);
            onMetaUpdate('notification_text', value);
          }}
        />
        <TextControl
          label={__('Read more label', '')}
          type="text"
          value={learnMoreLabel}
          onChange={(value) => {
            setLearnMoreLabel(value);
            onMetaUpdate('learn_more_label', value);
          }}
        />
        <SelectControl
          label={__('Read more type', '')}
          options={[
            { value: B3RG_IN_EX_LINK, label: 'Internal Link/External Link' },
            { value: B3RG_FI_LINK, label: 'File Link' },
            { value: B3RG_NONE, label: 'None' },
          ]}
          value={learnMoreType}
          onChange={(value) => {
            setLearnMoreType(value);
            onMetaUpdate('learn_more_type', value);
          }}
        />
        {learnMoreType === B3RG_IN_EX_LINK && (
          <InternalExternalLink
            {...{ learnMoreLinkMetaValue, onMetaUpdate, showPopupMetaValue }}
          />
        )}

        {learnMoreType === B3RG_FI_LINK && (
          <FileLink
            {...{
              onMetaUpdate,
              featuredImageUrl,
              learnMoreLinkFileMetaValue,
            }}
          />
        )}
        <NumberControl
          label={__('Expiration Time (in hours)', 'b3rg')}
          min={0}
          value={notificationExpirationTime}
          onChange={(value) => {
            const intValue = parseInt(value);
            setNotificationExpirationTime(intValue);
            onMetaUpdate('notification_expiration_time', intValue);
          }}
        />
      </PluginDocumentSettingPanel>
    );
  };

  const NotificationOptions = (props) => {
    const { postType } = props;
    if (!postType || 'notification' !== postType) {
      return null;
    }
    return <NotificationOptionsSettings {...props} />;
  };

  return withSelect((select) => {
    const postMeta = select('core/editor').getEditedPostAttribute('meta') || {};
    return {
      postMeta,
      currentPostId: select('core/editor').getCurrentPostId(),
      postType: select('core/editor').getCurrentPostType(),
    };
  })(NotificationOptions);
}

wp.domReady(() => {
  if (wp.editPost) {
    wp.plugins.registerPlugin('common-notification-options', {
      render: registerNotificationOptionsPlugin(wp),
      icon: 'bell',
    });
  }
});
